import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { BundledIconSource } from './bundled-icon-source.service';
import { HttpIconSource } from './http-icon-source.service';

/**
 * Result of an icon retrieval operation.
 */
export interface IconResult {
  svg: string | null;
  source: 'bundled' | 'cache' | 'http' | 'not-found';
}

/**
 * Orchestrates icon retrieval from multiple sources.
 * Follows the Chain of Responsibility pattern:
 * 1. Check bundled icons first (guaranteed availability)
 * 2. Check HTTP cache (already fetched)
 * 3. Fetch via HTTP (dynamic loading)
 */
@Injectable({
  providedIn: 'root'
})
export class IconService {
  constructor(
    private readonly bundledSource: BundledIconSource,
    private readonly httpSource: HttpIconSource
  ) {}

  /**
   * Retrieves an icon, checking bundled first, then HTTP.
   * @param name - Icon name (without extension)
   * @returns Observable with the SVG content and source info
   */
  getIcon(name: string): Observable<IconResult> {
    // 1. Check bundled icons first (synchronous, guaranteed)
    if (this.bundledSource.hasIcon(name)) {
      return of({
        svg: this.bundledSource.getIcon(name),
        source: 'bundled' as const
      });
    }

    // 2. Check HTTP cache (synchronous if cached)
    if (this.httpSource.hasIcon(name)) {
      return of({
        svg: this.httpSource.getIcon(name),
        source: 'cache' as const
      });
    }

    // 3. Fetch via HTTP (asynchronous)
    return new Observable<IconResult>((subscriber) => {
      this.httpSource.fetchIcon(name).subscribe({
        next: (svg) => {
          subscriber.next({
            svg,
            source: svg ? 'http' : 'not-found'
          });
          subscriber.complete();
        },
        error: (err) => {
          subscriber.next({ svg: null, source: 'not-found' });
          subscriber.complete();
        }
      });
    });
  }

  /**
   * Checks if an icon is available in bundled sources (synchronous).
   * Useful for determining if an icon will load instantly.
   */
  isBundled(name: string): boolean {
    return this.bundledSource.hasIcon(name);
  }

  /**
   * Checks if an icon is currently cached (bundled or HTTP).
   */
  isCached(name: string): boolean {
    return this.bundledSource.hasIcon(name) || this.httpSource.hasIcon(name);
  }
}
